import React, { useCallback, useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { MdOutlineRocketLaunch } from 'react-icons/md';
import { Container, Status } from './styles';
import { IEvent } from '..';
import { formatPrice } from '~/utils/format';
import { KrClose, KrEye, KrSearch } from '~/components/KoroIcons';
import api from '~/services/api';
import Toast from '~/utils/toast';

interface IBatch {
  quantity: number;
}

interface IOrder {
  total: string;
  items: {
    cancelled: boolean;
    id: number;
  }[];
}

interface ICard {
  event: IEvent;
}

const Card: React.FC<ICard> = ({ event: dataEvent }) => {
  const [event, setEvent] = useState({} as IEvent);

  const handleClickInactivate = useCallback(async (eventData) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: 'Deseja realmente inativar o evento?',
      icon: 'warning',
      cancelButtonText: 'Fechar',
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, inativar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await api.patch(`events/admin/inactivate/${eventData.id}`);

        setEvent((oldState) => ({
          ...oldState,
          inactive: !oldState.inactive,
          status: 'Cancelado',
        }));

        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Evento inativado com sucesso!',
        });
      }
    });
  }, []);

  const handleClickPriorize = useCallback(async (eventData) => {
    Swal.fire({
      title: `Gostaria de priorizar o evento ${eventData.title}?`,

      icon: 'warning',
      cancelButtonText: 'Não',
      reverseButtons: true,
      input: 'number',
      showCancelButton: true,
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim',
      inputAttributes: {
        min: '0',
        step: '1',
      },
      inputValue: eventData.priority || 0,
      inputValidator: (value) => {
        if (!value) {
          return 'Informe um valor de prioridade!';
        }
        return null;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const priority = Number(result.value);

        await api.patch(`events/admin/priority/${eventData.id}`, {
          priority,
        });

        setEvent((oldState) => ({
          ...oldState,
          priority,
        }));

        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Evento priorizado com sucesso!',
        });
      }
    });
  }, []);

  // test

  useEffect(() => {
    api
      .get<IOrder[]>(`orders/events/${dataEvent.id}`)
      .then(async (response) => {
        const responseBatches = await api.get<IBatch[]>(
          `batches/events/${dataEvent.id}`
        );

        const purchases = response.data.reduce((previous, current) => {
          return (
            previous + current.items.filter((item) => !item.cancelled).length
          );
        }, 0);

        const total = response.data.reduce((previous, current) => {
          return previous + parseFloat(current.total);
        }, 0);

        setEvent({
          ...dataEvent,
          purchases,
          total,
          avarage: Number.isNaN(total / purchases) ? 0 : total / purchases,
          allTickets: responseBatches.data.reduce((previous, current) => {
            return previous + current.quantity;
          }, 0),
        });
      });
  }, [dataEvent]);

  return (
    <Container className="d-flex bg-white justify-content-between align-items-center p-3 mt-3">
      <div className="w-auto flex-column align-items-center justify-content-center">
        <img src={event.archive_url} alt={event.title} className="default" />
        <p className="priority mb-0 text-center">
          ID: {event.id} <br />
          <span className=" d-block text-center">{event.priority ?? 0}</span>
        </p>
      </div>
      <div className="w-10">
        <h3 className="mb-1">Nome</h3>
        <p className="mb-0">{event.title}</p>
      </div>
      <div className="w-8">
        <h3 className="mb-1">Organizador</h3>
        <p className="mb-0">{event.organizer}</p>
      </div>
      <div className="w-7">
        <div className="d-flex flex-column align-items-center">
          <img
            src={event.type_image_url}
            alt={event.type}
            className="type-icon"
          />
          <p className="text-center mb-0">{event.type}</p>
        </div>
      </div>
      <div>
        <h3 className="mb-1">Data do evento</h3>
        <p className="mb-0">{event.date}</p>
      </div>
      <div className="w-10">
        <h3 className="mb-1">Status</h3>
        <Status status={event.status} className="text-center rounded-pill mb-0">
          {event.status === 'Esgotado' ? 'Fim vendas' : event.status}
        </Status>
      </div>
      <div>
        <h3 className="mb-1">Fim das inscrições</h3>
        <p className="mb-0">{event.endRegistration}</p>
      </div>
      <div>
        <h3 className="mb-1">Vendidos</h3>
        <p className="mb-0">{event.purchases}</p>
      </div>
      <div>
        <h3 className="mb-1">Vagas</h3>
        <p className="mb-0">
          {event.allTickets === 0 ? 'Ilimitadas' : event.allTickets}
        </p>
      </div>
      <div className="w-5">
        <h3 className="mb-1">Total R$</h3>
        <p className="mb-0">{formatPrice(event.total)}</p>
      </div>
      <div className="w-7">
        <h3 className="mb-1">Ticket Médio</h3>
        <p className="mb-0">{formatPrice(event.avarage)}</p>
      </div>
      <div>
        <h3 className="mb-1">Não listado</h3>
        <div className="form-check form-switch pe-none">
          <input
            className="form-check-input"
            type="checkbox"
            id="flexSwitchCheckDefault"
            checked={event.unlisted}
            disabled
          />
        </div>
      </div>
      <div>
        <button
          type="button"
          className="border-0 rounded-circle bg-orange me-2"
          onClick={() => handleClickPriorize(event)}
          // disabled={event.status === 'Cancelado'}
        >
          <MdOutlineRocketLaunch size={18} color="#fff" />
        </button>
        {event.status !== 'Cadastrando' && (
          <a
            href={`${process.env.REACT_APP_ATHLETE_URL}/eventos/${event.slug}/visualizacao`}
            className="me-2 border-0 rounded-circle bg-orange"
            target="_blank"
            rel="noreferrer"
          >
            <KrEye size={16} color="#fff" />
          </a>
        )}

        <Link
          to={`${process.env.PUBLIC_URL}/eventos/${event.slug}/resumo`}
          className="me-2 border-0 rounded-circle bg-orange"
        >
          <KrSearch size={16} color="#fff" />
        </Link>
        <button
          type="button"
          className="border-0 rounded-circle bg-orange"
          onClick={() => handleClickInactivate(event)}
          disabled={event.status === 'Cancelado'}
        >
          <KrClose size={16} color="#fff" />
        </button>
      </div>
    </Container>
  );
};

export default Card;
